import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { BellIcon, BuildingIcon, ChevronDownIcon, UserCircleIcon, CheckIcon, PanelLeftCloseIcon, PanelLeftOpenIcon } from 'lucide-react';
import { useMunicipality, municipalities } from '../hooks/useMunicipality';
import { useSidebar } from '../hooks/useSidebar';
const pageTitles: Record<string, string> = {
  '/': 'Dashboard',
  '/vending': 'Purchase Prepaid',
  '/payment-integrations': 'Payment Integrations',
  '/meters': 'Smart Meters',
  '/lpu': 'Large Power Users',
  '/map': 'Meter Map',
  '/consumption-analytics': 'Consumption Analytics',
  '/fault-detection': 'Fault Detection',
  '/vendors': 'Vendors',
  '/vendors/add': 'Add New Vendor',
  '/prepaid-revenue': 'Prepaid Revenue',
  '/lpu-revenue': 'LPU Revenue Management',
  '/tariff-management': 'Tariff Management',
  '/billing-integrations': 'Billing Integrations',
  '/sales-performance': 'Sales Performance',
  '/commissions': 'Commissions',
  '/campaigns': 'Campaign Programs',
  '/comm-channels': 'Communication Channels',
  '/substation-monitoring': 'Substation Monitoring',
  '/outage-detection': 'Outage & Theft Detection',
  '/network-diagnostics': 'Network Diagnostics',
  '/loss-analytics': 'Loss Analytics',
  '/outage-theft-detection': 'Outage & Theft Detection',
  '/settings': 'System Settings',
  '/support': 'Support',
  '/vendor-map': 'Vendor Map'
};
export function TopNav() {
  const location = useLocation();
  const currentPage = pageTitles[location.pathname] || 'Dashboard';
  const {
    selected: selectedMunicipality,
    setSelected: setSelectedMunicipality
  } = useMunicipality();
  const {
    collapsed,
    toggle
  } = useSidebar();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState<'fade-in' | 'visible' | 'fade-out'>('fade-in');
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSelectMunicipality = (municipality: typeof selectedMunicipality) => {
    if (municipality.id === selectedMunicipality.id) {
      setDropdownOpen(false);
      return;
    }
    setDropdownOpen(false);
    setIsLoading(true);
    setLoadingPhase('fade-in');
    // Fade in
    setTimeout(() => {
      setLoadingPhase('visible');
      setSelectedMunicipality(municipality);
    }, 400);
    // Hold visible
    setTimeout(() => {
      setLoadingPhase('fade-out');
    }, 1800);
    // Fade out and dismiss
    setTimeout(() => {
      setIsLoading(false);
      setLoadingPhase('fade-in');
    }, 2400);
  };
  return <>
      <div className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-6 shrink-0 z-10" data-id="element-247">
        {/* Left: Toggle + Breadcrumb */}
        <div className="flex items-center gap-3" data-id="element-248">
          <button onClick={toggle} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-none transition-colors" title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} data-id="element-249">

            {collapsed ? <PanelLeftOpenIcon className="w-5 h-5" data-id="element-250" /> : <PanelLeftCloseIcon className="w-5 h-5" data-id="element-251" />}
          </button>
          <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-252">
            <span className="text-gray-400" data-id="element-253">MAVIS</span>
            <span className="text-gray-300" data-id="element-254">/</span>
            <span className="font-medium text-gray-900" data-id="element-255">{currentPage}</span>
          </div>
        </div>

        {/* Right: Municipality Account + Actions */}
        <div className="flex items-center gap-5" data-id="element-256">
          {/* Municipality Dropdown */}
          <div className="relative" ref={dropdownRef} data-id="element-257">
            <button onClick={() => setDropdownOpen(!dropdownOpen)} className="flex items-center gap-2.5 bg-gray-50 border border-gray-200 rounded-none px-3 py-1.5 hover:bg-gray-100 transition-colors cursor-pointer" data-id="element-258">

              <div className="w-4 h-4 rounded-none flex items-center justify-center" style={{
              backgroundColor: selectedMunicipality.accentColor
            }} data-id="element-259">

                <BuildingIcon className="w-2.5 h-2.5 text-white" data-id="element-260" />
              </div>
              <div className="flex items-center gap-1.5" data-id="element-261">
                <span className="text-sm font-semibold text-gray-900" data-id="element-262">
                  {selectedMunicipality.name}
                </span>
                <ChevronDownIcon className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} data-id="element-263" />

              </div>
            </button>

            {/* Dropdown Menu */}
            {dropdownOpen && <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-gray-200 shadow-xl rounded-none z-50 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-264">
                <div className="p-3 border-b border-gray-100 bg-gray-50" data-id="element-265">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-266">
                    Switch Municipality
                  </p>
                </div>
                <div className="max-h-80 overflow-y-auto py-1" data-id="element-267">
                  {municipalities.map(muni => {
                const isSelected = muni.id === selectedMunicipality.id;
                return <button key={muni.id} onClick={() => handleSelectMunicipality(muni)} className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${isSelected ? 'bg-gray-50' : 'hover:bg-gray-50'}`} data-id="element-268">

                        <div className="w-8 h-8 rounded-none flex items-center justify-center shrink-0" style={{
                    backgroundColor: muni.accentColor
                  }} data-id="element-269">

                          <BuildingIcon className="w-4 h-4 text-white" data-id="element-270" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-271">
                          <p className={`text-sm truncate ${isSelected ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`} data-id="element-272">

                            {muni.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-273">
                            {muni.province} · {muni.meters} meters
                          </p>
                        </div>
                        {isSelected && <div className="w-5 h-5 rounded-none flex items-center justify-center shrink-0" style={{
                    backgroundColor: muni.accentColor
                  }} data-id="element-274">

                            <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-275" />
                          </div>}
                      </button>;
              })}
                </div>
              </div>}
          </div>

          {/* Notifications */}
          <button className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-none transition-colors" data-id="element-276">
            <BellIcon className="w-5 h-5" data-id="element-277" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-mavis-orange rounded-full" data-id="element-278"></span>
          </button>

          {/* User Avatar */}
          <div className="flex items-center gap-2 cursor-default" data-id="element-279">
            <div className="w-8 h-8 rounded-none flex items-center justify-center transition-colors duration-500" style={{
            backgroundColor: selectedMunicipality.accentColor
          }} data-id="element-280">

              <UserCircleIcon className="w-5 h-5 text-white" data-id="element-281" />
            </div>
            <div className="hidden lg:block" data-id="element-282">
              <p className="text-sm font-medium text-gray-900 leading-tight" data-id="element-283">
                Admin User
              </p>
              <p className="text-[10px] text-gray-500 leading-tight" data-id="element-284">
                System Administrator
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Full-screen Loading Overlay */}
      {isLoading && <div className={`fixed inset-0 z-[99999] flex flex-col items-center justify-center transition-opacity duration-500 ${loadingPhase === 'fade-in' ? 'opacity-0' : loadingPhase === 'visible' ? 'opacity-100' : 'opacity-0'}`} style={{
      backgroundColor: selectedMunicipality.accentColor
    }} data-id="element-285">

          <div className="flex flex-col items-center gap-6" data-id="element-286">
            <div className="bg-white p-4 rounded-none shadow-2xl" data-id="element-287">
              <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/s5dgpz2pomohbgjmvchnas/19620cb4-f500-4d30-868d-0c11080a1184/mavis_logo_letters.svg" alt="MAVIS Logo" className="h-16 w-auto object-contain" data-id="element-288" />

            </div>
            <div className="text-center" data-id="element-289">
              <h2 className="text-2xl font-bold text-white tracking-tight mb-2" data-id="element-290">
                MAVIS
              </h2>
              <p className="text-white/70 text-sm" data-id="element-291">
                Switching to {selectedMunicipality.name}...
              </p>
            </div>
            <div className="flex gap-2 mt-2" data-id="element-292">
              <div className="w-2 h-2 bg-white/60 rounded-full animate-pulse" style={{
            animationDelay: '0ms'
          }} data-id="element-293">
            </div>
              <div className="w-2 h-2 bg-white/60 rounded-full animate-pulse" style={{
            animationDelay: '200ms'
          }} data-id="element-294">
            </div>
              <div className="w-2 h-2 bg-white/60 rounded-full animate-pulse" style={{
            animationDelay: '400ms'
          }} data-id="element-295">
            </div>
            </div>
          </div>
        </div>}
    </>;
}